import React, { useEffect, useRef, useCallback } from 'react';
import { useGame } from '../../context/GameContext';
import { usePlayer } from '../../context/PlayerContext';
import ScoreBoard from '../ui/ScoreBoard';
import Button from '../ui/Button';
import { getWinnerMessage, getLoserMessage } from '../../utils/flavorText';
import { playVictoryFanfare } from '../../utils/sounds';

function GameOverScreen() {
  const { state, dispatch } = useGame();
  const { playerId, resetPlayer } = usePlayer();
  const { players, playerOrder } = state;

  const sorted = [...playerOrder].sort((a, b) => getTotal(players[a]) - getTotal(players[b]));
  const winnerId = sorted[0];
  const winnerName = players[winnerId]?.displayName || 'Unknown';
  const winnerTotal = getTotal(players[winnerId]);
  const runnerUpTotal = sorted.length > 1 ? getTotal(players[sorted[1]]) : winnerTotal;
  const margin = runnerUpTotal - winnerTotal;

  const isWinner = winnerId === playerId;
  const myRank = sorted.indexOf(playerId) + 1;

  const played = useRef(false);
  useEffect(() => {
    if (!played.current) {
      played.current = true;
      playVictoryFanfare();
      // Haptic feedback
      if (navigator.vibrate) {
        navigator.vibrate(isWinner ? [100, 50, 100, 50, 200] : [100]);
      }
    }
  }, [isWinner]);

  const handlePlayAgain = () => {
    resetPlayer();
    dispatch({ type: 'RESET' });
  };

  const handleShare = useCallback(async () => {
    const myTotal = getTotal(players[playerId]);
    const text = isWinner
      ? `I won Golf Card Game with ${myTotal} points! Can you beat that?`
      : `I scored ${myTotal} in Golf Card Game. ${winnerName} won with ${winnerTotal}. Think you can do better?`;
    const url = 'https://sixcardgolf.com';

    if (navigator.share) {
      try {
        await navigator.share({ title: 'Golf Card Game', text, url });
      } catch {}
    } else {
      try {
        await navigator.clipboard.writeText(`${text}\n${url}`);
        alert('Copied to clipboard!');
      } catch {}
    }
  }, [players, playerId, isWinner, winnerName, winnerTotal]);

  return (
    <div className="game-over-screen">
      <h2 className="game-over-screen__title">
        {getWinnerMessage(winnerName, margin)}
      </h2>

      <div className="game-over-screen__subtitle">
        {winnerTotal} points — lowest on the course today.
      </div>

      {isWinner ? (
        <div className="game-over-screen__personal">
          You did it! Champion of the clubhouse.
        </div>
      ) : (
        <div className="game-over-screen__personal">
          {getLoserMessage(myRank, sorted.length)}
        </div>
      )}

      <ScoreBoard />

      <div className="game-over-screen__actions">
        <Button onClick={handlePlayAgain}>
          Back to the Clubhouse
        </Button>
        <Button variant="secondary" onClick={handleShare}>
          Share Results
        </Button>
      </div>
    </div>
  );
}

function getTotal(player) {
  if (!player?.scores?.roundScores) return 0;
  return player.scores.roundScores.reduce((sum, s) => sum + (s ?? 0), 0);
}

export default GameOverScreen;
